import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import apiClient from '../api/api';
import { API_ENDPOINTS } from '../endpoints';
import withRetry from '../utils/withRetry';
import { getSessionStorageSafe } from '../utils/sessionStorage';

const initialState = {
  activeStep: 0,
  subjectHierarchy: {},
  courses: [],
  syllabuses: [],
  subjects: [],
  units: [],
  modules: [],
  topics: [],
  selectedCourse: '',
  selectedSyllabus: '',
  selectedSubject: '',
  selectedUnit: '',
  selectedModule: '',
  selectedTopic: '',
  initialLoading: true,
  pathwayLoading: false,
  pathwayData: [],
  pathwayCache: {},
};

const useCreateTestPathwayStore = create(
  persist(
    (set, get) => ({
      ...initialState,
      setActiveStep: (step) => set({ activeStep: step }),

      fetchInitialData: async () => {
        try {
          set({ initialLoading: true });
          const response = await apiClient.get(API_ENDPOINTS.getDashboard);
          const data = response.data;
          const hierarchy = {};
          (data.availSubjects || []).forEach((sub) => {
            if (!hierarchy[sub.courseCode]) hierarchy[sub.courseCode] = {};
            if (!hierarchy[sub.courseCode][sub.sylName]) hierarchy[sub.courseCode][sub.sylName] = [];
            hierarchy[sub.courseCode][sub.sylName].push(sub.name);
          });
          set({ subjectHierarchy: hierarchy, courses: Object.keys(hierarchy), initialLoading: false });
        } catch (error) {
          console.error('Failed to fetch initial data', error);
          set({ initialLoading: false });
        }
      },

      selectCourse: (course) => {
        const { subjectHierarchy, activeStep } = get();
        const syllabuses = subjectHierarchy[course] ? Object.keys(subjectHierarchy[course]) : [];
        set({
          selectedCourse: course,
          syllabuses,
          selectedSyllabus: '',
          subjects: [],
          selectedSubject: '',
          units: [],
          selectedUnit: '',
          modules: [],
          selectedModule: '',
          topics: [],
          selectedTopic: '',
          pathwayData: [],
          pathwayLoading: false,
          // Preserve activeStep and other important state
          activeStep,
        });
      },

      selectSyllabus: (syllabus) => {
        const { subjectHierarchy, selectedCourse } = get();
        const subjects = subjectHierarchy[selectedCourse]?.[syllabus] || [];
        set({
          selectedSyllabus: syllabus,
          subjects,
          selectedSubject: '',
          units: [],
          selectedUnit: '',
          modules: [],
          selectedModule: '',
          topics: [],
          selectedTopic: '',
        });
      },

      selectSubject: async (subject) => {
        set({ selectedSubject: subject, pathwayLoading: true, selectedUnit: '', selectedModule: '', selectedTopic: '' });
        const { selectedCourse, selectedSyllabus } = get();
        const cacheKey = `${selectedCourse}|${selectedSyllabus}|${subject}`;
        const cached = get().pathwayCache[cacheKey];
        if (cached) {
          const units = [...new Set(cached.map((p) => p.unitCode))];
          set({ pathwayData: cached, units, pathwayLoading: false });
          return;
        }
        try {
          const url = API_ENDPOINTS.getPathway(selectedCourse, selectedSyllabus, subject);
          const response = await withRetry(() => apiClient.get(url));
          const pathwayData = response.data || [];
          set((state) => ({
            pathwayCache: { ...state.pathwayCache, [cacheKey]: pathwayData },
          }));
          const units = [...new Set(pathwayData.map((p) => p.unitCode))];
          set({ pathwayData, units, pathwayLoading: false });
        } catch (error) {
          console.error('Failed to fetch pathway data', error);
          set({ pathwayLoading: false });
        }
      },

      selectUnit: (unit) => {
        const { pathwayData } = get();
        const modules = [...new Set(pathwayData.filter((p) => p.unitCode === unit).map((p) => p.moduleCode))];
        set({ selectedUnit: unit, modules, selectedModule: '', topics: [], selectedTopic: '' });
      },

      selectModule: (module) => {
        const { pathwayData, selectedUnit } = get();
        const topics = pathwayData.filter((p) => p.unitCode === selectedUnit && p.moduleCode === module).map((p) => p.topicCode);
        set({ selectedModule: module, topics, selectedTopic: '' });
      },

      selectTopic: (topic) => set({ selectedTopic: topic }),

      // Add new pathway items
      addNewCourse: (course) => {
        const { courses, subjectHierarchy } = get();
        if (!courses.includes(course)) {
          set({
            courses: [...courses, course],
            subjectHierarchy: { ...subjectHierarchy, [course]: {} },
            selectedCourse: course,
            syllabuses: [],
            selectedSyllabus: '',
            subjects: [],
            selectedSubject: '',
          });
        }
      },

      addNewSyllabus: (syllabus) => {
        const { selectedCourse, subjectHierarchy, syllabuses } = get();
        if (selectedCourse && !syllabuses.includes(syllabus)) {
          const updatedHierarchy = {
            ...subjectHierarchy,
            [selectedCourse]: {
              ...subjectHierarchy[selectedCourse],
              [syllabus]: []
            }
          };
          set({
            subjectHierarchy: updatedHierarchy,
            syllabuses: [...syllabuses, syllabus],
            selectedSyllabus: syllabus,
            subjects: [],
            selectedSubject: '',
          });
        }
      },

      addNewSubject: (subject) => {
        const { selectedCourse, selectedSyllabus, subjectHierarchy, subjects } = get();
        if (selectedCourse && selectedSyllabus && !subjects.includes(subject)) {
          const updatedHierarchy = {
            ...subjectHierarchy,
            [selectedCourse]: {
              ...subjectHierarchy[selectedCourse],
              [selectedSyllabus]: [...(subjectHierarchy[selectedCourse][selectedSyllabus] || []), subject]
            }
          };
          set({
            subjectHierarchy: updatedHierarchy,
            subjects: [...subjects, subject],
            selectedSubject: subject,
          });
        }
      },

      addNewUnit: (unit) => {
        const { units } = get();
        if (!units.includes(unit)) {
          set({
            units: [...units, unit],
            selectedUnit: unit,
            modules: [],
            selectedModule: '',
            topics: [],
            selectedTopic: '',
          });
        }
      },

      addNewModule: (module) => {
        const { modules } = get();
        if (!modules.includes(module)) {
          set({
            modules: [...modules, module],
            selectedModule: module,
            topics: [],
            selectedTopic: '',
          });
        }
      },

      addNewTopic: (topic) => {
        const { topics } = get();
        if (!topics.includes(topic)) {
          set({
            topics: [...topics, topic],
            selectedTopic: topic,
          });
        }
      },

      reset: () => set(initialState),
    }),
    {
      name: 'create-test-pathway',
      storage: createJSONStorage(getSessionStorageSafe),
      partialize: (state) => ({
        activeStep: state.activeStep,
        subjectHierarchy: state.subjectHierarchy,
        courses: state.courses,
        syllabuses: state.syllabuses,
        subjects: state.subjects,
        units: state.units,
        modules: state.modules,
        topics: state.topics,
        selectedCourse: state.selectedCourse,
        selectedSyllabus: state.selectedSyllabus,
        selectedSubject: state.selectedSubject,
        selectedUnit: state.selectedUnit,
        selectedModule: state.selectedModule,
        selectedTopic: state.selectedTopic,
        pathwayData: state.pathwayData,
        pathwayCache: state.pathwayCache,
        initialLoading: state.initialLoading,
        pathwayLoading: state.pathwayLoading,
      }),
    }
  )
);

export default useCreateTestPathwayStore;
